import { db } from '../firebase.js';
import { doc, getDoc } from 'firebase/firestore';

// Cached config data — loaded once, reused for session
let _funnel = null;
let _disorders = null;
let _trackers = null;
let _meetingTypes = null;

export async function loadFunnelConfig() {
  if (_funnel) return _funnel;
  const snap = await getDoc(doc(db, 'config', 'funnel'));
  _funnel = snap.exists() ? snap.data() : null;
  // Backfill imputedStatementsByCategory from bundled defaults if the seeded doc predates this field.
  if (_funnel && (!_funnel.imputedStatementsByCategory || !Object.keys(_funnel.imputedStatementsByCategory).length)) {
    try {
      const { default: funnelData } = await import('../data/funnel-data-full.js');
      _funnel.imputedStatementsByCategory = funnelData.imputedStatementsByCategory || {};
    } catch {}
  }
  return _funnel;
}

export async function loadDisordersConfig() {
  if (_disorders) return _disorders;
  const snap = await getDoc(doc(db, 'config', 'disorders'));
  _disorders = snap.exists() ? snap.data() : null;
  return _disorders;
}

export async function loadTrackersConfig() {
  if (_trackers) return _trackers;
  const snap = await getDoc(doc(db, 'config', 'trackers'));
  _trackers = snap.exists() ? snap.data() : null;
  return _trackers;
}

export async function loadMeetingTypesConfig() {
  if (_meetingTypes) return _meetingTypes;
  const snap = await getDoc(doc(db, 'config', 'meetingTypes'));
  // A school whose /config/meetingTypes doc was never seeded must STILL get the
  // bundled meeting/plan definitions — otherwise meetings can't be opened or
  // created ("type not found in config"). Start from the Firestore doc when it
  // exists, else an empty shell, then always overlay the bundled MEETING_TYPES.
  const data = snap.exists() ? snap.data() : { types: {} };
  // Deserialize meeting types if they were stored as JSON strings (Firestore nested array workaround)
  if (data._serialized && data.types) {
    const parsed = {};
    Object.entries(data.types).forEach(([k, v]) => {
      try { parsed[k] = typeof v === 'string' ? JSON.parse(v) : v; } catch { parsed[k] = v; }
    });
    data.types = parsed;
    delete data._serialized;
  }
  // Overlay bundled plan/meeting type definitions on top of the Firestore doc so structural
  // edits to plans (renamed/removed sections, new field types) ship with the app without
  // forcing every school to re-seed. Firestore-side overrides are not yet supported — when
  // we add a "custom plan editor" we'll switch this back to Firestore-wins.
  try {
    const { MEETING_TYPES, MEETING_ACKNOWLEDGEMENTS } = await import('../data/seed-data.js');
    if (MEETING_TYPES) {
      data.types = { ...(data.types || {}), ...MEETING_TYPES };
    }
    // Same rationale as types: ship acknowledgement wording with the app so a
    // deploy updates every school without re-seeding Firestore. Bundled wins.
    if (Array.isArray(MEETING_ACKNOWLEDGEMENTS)) {
      data.acknowledgements = MEETING_ACKNOWLEDGEMENTS;
    }
  } catch {}
  // If we still have no types at all (bundled import failed AND no Firestore
  // doc), return null so callers keep their existing "not seeded" handling.
  if (!data.types || !Object.keys(data.types).length) { _meetingTypes = null; return null; }
  _meetingTypes = data;
  return _meetingTypes;
}

export async function loadAllConfig() {
  const [funnel, disorders, trackers, meetingTypes] = await Promise.all([
    loadFunnelConfig(),
    loadDisordersConfig(),
    loadTrackersConfig(),
    loadMeetingTypesConfig(),
  ]);
  // Also load note categories + goal-plan catalogue (both editable
  // through Config Editor). Missing doc → null fallback; renderers
  // already cope with that.
  let noteCategories = null;
  let goals = null;
  try {
    const { getDoc, doc } = await import('firebase/firestore');
    const { db } = await import('../firebase.js');
    const [ncSnap, goalsSnap] = await Promise.all([
      getDoc(doc(db, 'config', 'noteCategories')),
      getDoc(doc(db, 'config', 'goals')),
    ]);
    if (ncSnap.exists()) noteCategories = ncSnap.data();
    if (goalsSnap.exists()) goals = goalsSnap.data();
  } catch {}
  return { funnel, disorders, trackers, meetingTypes, noteCategories, goals };
}

// Clear cache (useful after admin edits disorders)
export function clearConfigCache() {
  _funnel = null;
  _disorders = null;
  _trackers = null;
  _meetingTypes = null;
}
